import type {
  IframeConfig,
  IframeVisibility,
  IframeMessage,
  MessageHandler,
  IParentIframeManager,
} from "./types";
import { debug, setDebugCallback } from "./debug";

export class ParentIframeManager implements IParentIframeManager {
  private iframe: HTMLIFrameElement | null = null;
  private config: IframeConfig | null = null;
  private messageHandlers: MessageHandler[] = [];
  private messageListener: ((event: MessageEvent) => void) | null = null;

  async initialize(config: IframeConfig): Promise<void> {
    this.config = config;

    // Set up debug callback if provided
    if (config.debug) {
      setDebugCallback(config.debug);
    }

    const container =
      typeof config.container === "string"
        ? document.querySelector(config.container)
        : config.container;

    if (!container) throw new Error(`Container not found: ${config.container}`);

    this.iframe = document.createElement("iframe");
    this.iframe.src = `${config.baseUrl}${config.initialRoute || ""}`;
    this.iframe.style.cssText =
      "width:100%;height:100vh;border:none;position:absolute;top:0;left:0;z-index:1000;display:none";

    container.appendChild(this.iframe);
    this.setupMessageListener();
    this.setVisibility(config.initialVisibility || "hidden");

    debug(
      "[PaywallParent] iframe-messenger-v2: ParentIframeManager initialized",
      { baseUrl: config.baseUrl, initialRoute: config.initialRoute }
    );
  }

  setVisibility(visibility: IframeVisibility): void {
    if (this.iframe) {
      this.iframe.style.display = visibility === "visible" ? "block" : "none";
      // Enable/disable pointer events on both iframe and its container
      this.iframe.style.pointerEvents =
        visibility === "visible" ? "auto" : "none";

      // Also update container pointer events if it exists
      const container =
        typeof this.config?.container === "string"
          ? document.querySelector(this.config.container)
          : this.config?.container;
      if (container && container instanceof HTMLElement) {
        container.style.pointerEvents =
          visibility === "visible" ? "auto" : "none";
      }
    }
  }

  navigate(route: string): void {
    if (this.iframe && this.config) {
      this.iframe.src = `${this.config.baseUrl}${route}`;
    }
  }

  sendMessageToChild(message: IframeMessage): void {
    if (this.iframe?.contentWindow && this.config) {
      this.iframe.contentWindow.postMessage(
        message,
        this.config.trustedOrigins?.[0] || "*"
      );
    }
  }

  onMessageFromChild(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) this.messageHandlers.splice(index, 1);
    };
  }

  isReady(): boolean {
    return !!this.iframe;
  }

  getVisibility(): IframeVisibility {
    return this.iframe?.style.display === "block" ? "visible" : "hidden";
  }

  destroy(): void {
    if (this.messageListener) {
      window.removeEventListener("message", this.messageListener);
    }
    this.iframe?.remove();
    this.iframe = null;
    this.messageHandlers = [];
  }

  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {
      if (
        this.config?.trustedOrigins?.length &&
        !this.config.trustedOrigins.includes(event.origin)
      )
        return;

      if (event.data?.type) {
        this.messageHandlers.forEach((handler) => handler(event.data));
      }
    };
    window.addEventListener("message", this.messageListener);
  }
}
